import { Image, StyleSheet, View } from "react-native";
import { useEffect } from "react";
import { Pad, PadBox } from "component/basics";
import { UtilityText, WebLinkTextButton } from "component/text";
import { colorTextPrimary, colorTextSecondary } from "component/color";
import { Close } from '@carbon/icons-react';
import { useDatastore, useGlobalParams, useModuleObject } from "util/datastore";
import { urlToKey } from "util/util";
import { ActionButton } from "component/button";
import { useTranslation } from "component/translation";
import { logEventAsync } from "util/eventlog";
import { Feature, undefF } from "util/stdtypes";
import { QuestionConfig } from "structure/question";
import { defineModuleMap } from "util/moduledata";

export const ContextEntryPointsFeature : Feature<QuestionConfig, {}> = {
    name: 'Context Entry Points',
    key: 'context_entry_points',
    providesConfig: {
        composerTopWidgets: [ComposerEntryPointCard],
        commentMiddleWidgets: [PostedCommentEntryPointCard],
    }
}

export function ComposerEntryPointCard({comment, setComment}) {
    const {entryUrl} = useGlobalParams();
    if (comment.hideEntryPoint || !entryUrl) {
        return null;
    }

    function onClose() {
        setComment({ ...comment, hideEntryPoint: true });
    }

    return <PadBox bottom={40}><ContextEntryPointCard entryUrl={entryUrl} onClose={onClose} /></PadBox>
}

export function PostedCommentEntryPointCard({comment}) {
    const entryUrl = comment.entryUrl || comment.entryPoints?.[0].url;
    if (!entryUrl || comment.hideEntryPoint) return null;

    return <PadBox top={16}>
        <ContextEntryPointCard entryUrl={entryUrl} />
    </PadBox>
}


// HACK (Rob): This shouldn't really be defined here. Move it to the article module.
export type ArticleInfo = {
    title: string;
    missing?: boolean;
    image?: string,
    author?: string,
    date?: string
}
// eslint-disable-next-line module-data-owner -- FIXME: use module name 'context-entry-points'
export const moduleMapArticleInfo = defineModuleMap<ArticleInfo>('PUBLIC', 'article', 'articleInfo');

const activeConstructors = {};

function useArticleInfo({entryUrl}) {
    const articleKey = urlToKey(entryUrl);
    const structureKey = 'article';
    const datastore = useDatastore();
    const articleInfo = useModuleObject(moduleMapArticleInfo, articleKey);
    useEffect(() => {
        if (!articleInfo && !activeConstructors[articleKey]) {
            datastore.callServerAsync('constructor', 'runConstructor', {
                structureKey, instanceKey: articleKey
            });
            activeConstructors[articleKey] = true;
        }
    }, [articleInfo])
    return articleInfo
}

function ContextEntryPointCard({entryUrl, onClose=undefF}) {
    const s = ContextEntryPointCardStyle;
    const articleInfo = useArticleInfo({entryUrl});
    const ariaLabel = useTranslation("Joined from") + " " + articleInfo?.title;
    const datastore = useDatastore();

    
    if (!entryUrl || !articleInfo || articleInfo.missing) return null;
    
    const onLinkPress = () => {
        logEventAsync(datastore, 'context_entry_point_click', {entryUrl});
    }

    return <View>
        <Pad size={onClose ? 16 : 8} />
        <View style={s.outerBox} aria-label={ ariaLabel}>
            <Image source={{ uri: articleInfo?.image }} style={s.image} aria-hidden />
            <Pad size={8} />
            <View style={s.textContainer}>
                <UtilityText type='tiny' weight='regular' label='Joined from' color={colorTextSecondary} />
                <WebLinkTextButton url={entryUrl} type='small' weight='medium' text={articleInfo?.title} numberOfLines={1} onLinkPress={onLinkPress} />
            </View>
            <Pad size={8} />
            {onClose && <ActionButton icon={Close} onPress={onClose} ariaLabel='Do not associate the source article with your response' />}
        </View>
    </View>
}
const ContextEntryPointCardStyle = StyleSheet.create({
    outerBox: {
        width: '100%',
        borderRadius: 8,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: 36,
    },
    image: {
        height: 36,
        width: 36,
        borderRadius: 8,
    },
    textContainer: {
        flex: 1
    }
});

